import React, { useEffect, useState } from "react";
import "../styles/ItemModels.css";
import "../styles/GrnTableStyles.css";
import { deleteData, getData, postData, patchData, getDataById } from "../api/ApiServices";
import { ApiResponse } from "../models/NewGrn";
import { SupplierResponse } from "../models/SupplierParts";
import AddPurchaseOrderForm from "../forms/AddPurchaseOrderForm";
import { ContributorResponse } from "../models/contributortypes";
import PopupConfirmation from "../view/common/alert/PopupConfirmation";
import { Trash, Edit } from "lucide-react";
import "../styles/grnformvalidation.css";
import { resetFilters } from "../redux/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";
import useFormattedDate from "../utils/useFormatDate";

interface PurchaseOrder {
  _id?: string;
  poNo: string;
  poDate: string;
  supplierId: string;
  partId: string;
  qty: number | string;
  unitPrice: number;
  total: number;
  gstName: string;
  sgst: number;
  cgst: number;
  igst: number;
  gstTotal: number;
  netTotal: number;
  status: string;
}

const PurchaseOrder: React.FC = () => {
  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [suppliers, setSuppliers] = useState<ContributorResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [parts, setParts] = useState<any>([]);
  const [poNo, setPoNo] = useState<string>("");
  const [tableData, setTableData] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const [prefilledData, setPrefilledData] = useState({
    supplierId: "",
    poNumber: "",
    readOnly: false,
    paymentTerms: "",
    dueDate: ""
  });

  const [isReadOnly, setIsReadOnly] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string>("");
  const [initializePO, isPOInitialized] = useState(false);
  const dispatch = useDispatch();
  const permission = useSelector((state: RootState) => state.permissions);

  const { formatDateForInput } = useFormattedDate();

  // Calculate table totals
  const calculateTotals = () => {
    if (tableData.length === 0) return { grossTotal: 0, gstTotal: 0, netTotal: 0 };
    
    return tableData.reduce((acc, item) => {
      return {
        grossTotal: acc.grossTotal + (item.total || 0),
        gstTotal: acc.gstTotal + (item.gstTotal || 0),
        netTotal: acc.netTotal + (item.netTotal || 0)
      };
    }, { grossTotal: 0, gstTotal: 0, netTotal: 0 });
  };

  // Get calculated totals
  const totals = calculateTotals();

  useEffect(() => {
    dispatch(resetFilters());
    isPOInitialized(true);
  }, [dispatch]);

  const handleAddOrEdit = async (po: PurchaseOrder) => {
    setIsProcessing(true);
    try {
      let response: any;

      if (isEditing && editingPO?._id) {
        // Update the purchase order
        await patchData(`/purchase-orders`, editingPO._id, { ...po });
        
        // Get updated data
        response = await getDataById("/purchase-orders", editingPO?._id);
        
        if (response.data && response.data.length > 0) {
          let updatedPO = response.data[0];
          
          // Calculate values
          const total = updatedPO.unitPrice * updatedPO.qty;
          const sgstAmount = (total * updatedPO.sgst) / 100;
          const cgstAmount = (total * updatedPO.cgst) / 100;
          const igstAmount = (total * updatedPO.igst) / 100;
          const gstTotal = sgstAmount + cgstAmount + igstAmount;

          setPrefilledData({
            supplierId: updatedPO.supplierId || "",
            poNumber: updatedPO.poNumber || "",
            readOnly: true,
            paymentTerms: updatedPO.paymentTerms || "",
            dueDate: updatedPO.dueDate
          });
          
          // Update the table data with the new values
          setTableData((prevTableData) =>
            prevTableData.map((item) => {
              if (item._id === editingPO._id) {
                return {
                  ...updatedPO,
                  total: total,
                  gstTotal: gstTotal,
                  netTotal: total + gstTotal
                };
              }
              return item;
            })
          );
        }

        

        setIsEditing(false);
        setEditingPO(null);
        setPopupMessage("Purchase Order updated successfully");
        
      } else {
        // Create new purchase order
        const poData: any = await postData("/purchase-orders", po);
        
        // Get the created purchase order details
        response = await getDataById("/purchase-orders", poData?.data._id);
        
        if (response.data && response.data.length > 0) {
          let newPO = response.data[0];
          
          // Calculate values
          const total = newPO.unitPrice * newPO.qty;
          const sgstAmount = (total * newPO.sgst) / 100;
          const cgstAmount = (total * newPO.cgst) / 100;
          const igstAmount = (total * newPO.igst) / 100;
          const gstTotal = sgstAmount + cgstAmount + igstAmount;
          
          // Add to table with calculated values
          setTableData((prevTableData) => [
            ...prevTableData, 
            {
              ...newPO,
              total: total,
              gstTotal: gstTotal,
              netTotal: total + gstTotal
            }
          ]);

          if (!poNo) {
            setPoNo(newPO.poNumber);
          }

          setPrefilledData({
            supplierId: newPO.supplierId || "",
            poNumber: newPO.poNumber || "",
            readOnly: true,
            paymentTerms: newPO.paymentTerms || "",
            dueDate: newPO.dueDate
          });

          setIsReadOnly(true);
        }

        setPopupMessage("Purchase Order saved successfully");
      }

      setPopupType("success");
      setIsConfirmation(false);
      setShowPopup(true);
    } catch (error) {
      console.error("Error saving/updating Purchase Order:", error);

      setPopupMessage(
        isEditing
          ? "Error updating Purchase Order. Please try again."
          : "Duplicate Entry.."
      );
      setPopupType("error");
      setIsConfirmation(false);
      setShowPopup(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleComplete = () => {
    setTableData([]);
    setPoNo("");
    setPrefilledData({ supplierId: "", poNumber: "", readOnly: false, paymentTerms: "", dueDate: "" });
    setIsReadOnly(false);
    setIsEditing(false);
    setEditingPO(null);

    setPopupMessage("Purchase Order process completed successfully");
    setPopupType("success");
    setIsConfirmation(false);
    setShowPopup(true);
  };

  const confirmDelete = (id: string) => {
    if (isEditing) {
      setPopupMessage("Please complete the current editing process before deleting any records.");
      setPopupType("warning");
      setIsConfirmation(false);
      setShowPopup(true);
      return;
    }

    setPopupMessage("Are you sure you want to delete this Purchase Order?");
    setPopupType("warning");
    setIsConfirmation(true);
    setItemToDelete(id);
    setShowPopup(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteData("/purchase-orders", itemToDelete);

      setTableData((prev) => prev.filter((item) => item._id !== itemToDelete));
      setPopupMessage("Purchase Order deleted successfully");
      setPopupType("success");
      setIsConfirmation(false);
      setShowPopup(true);
    } catch (error) {
      console.error("Error deleting Purchase Order:", error);

      setPopupMessage("Error deleting Purchase Order. Please try again.");
      setPopupType("error");
      setIsConfirmation(false);
      setShowPopup(true);
    }
  };

  const handleEdit = (po: PurchaseOrder) => {
    console.log("editing po", po);
    
    const poToEdit = JSON.parse(JSON.stringify(po));

    if (poToEdit.poDate) {
      poToEdit.poDate = formatDateForInput(poToEdit.poDate);
    }

    // Convert object IDs to string IDs if needed
    if (
      poToEdit.partId &&
      typeof poToEdit.partId === "object" &&
      poToEdit.partId._id
    ) {
      poToEdit.partId = poToEdit.partId._id;
    }

    // Important fix: Extract supplierId properly from the supplier object if needed
    if (
      typeof poToEdit.supplierId === "object" &&
      poToEdit.supplierId._id
    ) {
      poToEdit.supplierId = poToEdit.supplierId._id;
    }

    setEditingPO(poToEdit);
    setIsEditing(true);
    setIsReadOnly(true);

    // Set the prefilled data with the correct supplierId
    setPrefilledData({
      supplierId: typeof poToEdit.supplierId === 'string' ? poToEdit.supplierId : "",
      poNumber: poToEdit.poNumber || "",
      readOnly: true,
      paymentTerms: poToEdit.paymentTerms || "",
      dueDate: poToEdit.dueDate
    });

    setPoNo(poToEdit.poNumber || "");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setEditingPO(null);
    setIsEditing(false);

    setPrefilledData((prev) => ({
      ...prev,
      readOnly: true,
    }));
  };

  return (
    <div className="grn-container">
      <div className="grn-form-wrapper">
        <AddPurchaseOrderForm
          onSave={handleAddOrEdit}
          onCancel={handleCancel}
          isEditing={isEditing}
          editingPO={editingPO}
          poNumber={poNo}
          prefilledData={prefilledData}
          isReadOnly={isReadOnly}
          isProcessing={isProcessing}
        />
      </div>

      <div className="item-models-table-wrapper">
        <table className="item-models-table grn-table">
          <thead>
            <tr>
              <th>PO No</th>
              <th>Part No</th>
              <th>Part Name</th>
              <th>Quantity(UOM)</th>
              <th>Unit Price</th>
              <th>Due Date</th>
              <th>Total</th>
              <th>SGST</th>
              <th>CGST</th>
              <th>IGST</th>
              <th>Gst Total</th>
              <th>Net Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((item, index) => {
                const partDetails = item.parts;
                
                return (
                  <tr key={index}>
                    <td>{item.poNumber}</td>
                    <td>{partDetails?.internalPartNo || "N/A"}</td>
                    <td>{partDetails?.partName || "N/A"}</td>
                    <td className="grn-right-align">
                      {item.qty}({partDetails?.uomId?.name})
                    </td>
                    <td className="grn-right-align">{item.unitPrice}</td>
                    <td className="grn-right-align">{formatDateForInput(item.dueDate)}</td>
                    <td className="grn-right-align">{item.total}</td>
                    <td className="grn-right-align">{item.sgst}</td>
                    <td className="grn-right-align">{item.cgst}</td>
                    <td className="grn-right-align">{item.igst}</td>
                    <td className="grn-right-align">{item.gstTotal}</td>
                    <td className="grn-right-align">{item.netTotal}</td>
                    <td>
                      <div className="action-buttons">
                        {permission.menus["purchase-orders"]?.update && (
                          <button
                            className="edit-btn"
                            onClick={() => handleEdit(item)}
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        {permission.menus["purchase-orders"]?.delete && (
                          <button
                            className="delete-btn"
                            onClick={() => confirmDelete(item._id || "")}
                          >
                            <Trash size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={13} style={{ textAlign: "center" }}>
                  No Purchase Orders added
                </td>
              </tr>
            )}
          </tbody>
          {tableData.length > 0 && (
            <tfoot>
              <tr className="total-row">
                <td colSpan={6} className="total-label">Grand Total:</td>
                <td className="grn-right-align">{totals.grossTotal.toFixed(2)}</td>
                <td colSpan={3}></td>
                <td className="grn-right-align">{totals.gstTotal.toFixed(2)}</td>
                <td className="grn-right-align">{totals.netTotal.toFixed(2)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>

        {tableData.length > 0 && (
          <button
            className="grn-complete-button"
            onClick={handleComplete}
            disabled={isEditing}
          >
            Complete
          </button>
        )}
      </div>

      <PopupConfirmation
        isOpen={showPopup}
        message={popupMessage}
        type={popupType}
        onClose={() => setShowPopup(false)}
        autoCloseTime={3000}
        onConfirm={isConfirmation ? handleDelete : undefined}
        isConfirmation={isConfirmation}
      />
    </div>
  );
};

export default PurchaseOrder;